import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdNotifications, MdCheckCircle, MdClose, MdPoll, MdStar, MdFlag, MdSecurity, MdPerson, MdInfo, MdDelete, MdDoneAll, MdFilterList, MdEmojiEvents } from 'react-icons/md';

const ICON_MAP = {
  poll_approved: MdCheckCircle,
  poll_rejected: MdClose,
  poll_flagged: MdFlag,
  poll_closed: MdPoll,
  badge: MdEmojiEvents,
  achievement: MdEmojiEvents,
  milestone: MdStar,
  security: MdSecurity,
  profile_reset: MdPerson,
  welcome: MdStar,
};

const COLOR_MAP = {
  poll_approved: { icon: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200' },
  poll_rejected: { icon: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' },
  poll_flagged: { icon: 'text-orange-500', bg: 'bg-orange-50', border: 'border-orange-200' },
  poll_closed: { icon: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200' },
  badge: { icon: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  achievement: { icon: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  milestone: { icon: 'text-purple-500', bg: 'bg-purple-50', border: 'border-purple-200' },
  security: { icon: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' },
  profile_reset: { icon: 'text-purple-500', bg: 'bg-purple-50', border: 'border-purple-200' },
  welcome: { icon: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200' },
};

const TYPE_LABELS = {
  poll_approved: 'Poll Approved',
  poll_rejected: 'Poll Rejected',
  poll_flagged: 'Poll Flagged',
  poll_closed: 'Poll Closed',
  badge: 'Badge Earned',
  achievement: 'Achievement',
  milestone: 'Milestone',
  security: 'Security',
  profile_reset: 'Profile',
  welcome: 'Welcome',
};

function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      if (filter === 'unread') params.append('unread_only', 'true');
      const res = await axios.get(`${API_URL}/api/notifications?${params}`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/mark-read`, { notification_ids: [id] });
      setNotifications(prev => prev.map(n => n.notificationId === id ? { ...n, isRead: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {}
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.notificationId !== id));
    } catch {}
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get unique notification types for filter
  const uniqueTypes = [...new Set(notifications.map(n => n.type))];

  const filtered = notifications.filter(n => {
    if (filter === 'read' && !n.isRead) return false;
    if (typeFilter !== 'all' && n.type !== typeFilter) return false;
    return true;
  });

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-rubik flex items-center gap-2">
            <MdNotifications className="text-primary" />
            Notifications
          </h1>
          <p className="text-sm text-gray-500 mt-1 font-rubik">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors"
            data-testid="mark-all-read-page-btn"
          >
            <MdDoneAll className="text-base" />
            Mark all as read
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="notification-filters">
        {/* Read status filter */}
        <div className="flex bg-gray-100 rounded-lg p-0.5 text-xs font-medium">
          {['all', 'unread', 'read'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md transition-colors capitalize ${filter === f ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Type filter */}
        {uniqueTypes.length > 1 && (
          <div className="flex items-center gap-1.5">
            <MdFilterList className="text-gray-400 text-sm" />
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 focus:ring-2 focus:ring-primary focus:border-primary"
              data-testid="type-filter-select"
            >
              <option value="all">All types</option>
              {uniqueTypes.map(t => (
                <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Notification List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16" data-testid="notifications-empty">
          <MdNotifications className="mx-auto text-5xl text-gray-200 mb-4" />
          <p className="text-gray-400 font-rubik text-lg">
            {filter === 'unread' ? 'No unread notifications' : filter === 'read' ? 'No read notifications' : 'No notifications yet'}
          </p>
          <p className="text-gray-300 text-sm mt-1 font-rubik">Notifications will appear here as you use the app</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="notifications-list">
          {filtered.map(notif => {
            const Icon = ICON_MAP[notif.type] || MdInfo;
            const colors = COLOR_MAP[notif.type] || { icon: 'text-gray-500', bg: 'bg-gray-50', border: 'border-gray-200' };

            return (
              <div
                key={notif.notificationId}
                className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${colors.border} ${
                  !notif.isRead ? `${colors.bg} shadow-sm` : 'bg-white hover:bg-gray-50'
                }`}
                data-testid={`notification-${notif.notificationId}`}
              >
                {/* Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
                  <Icon className={`text-xl ${colors.icon}`} />
                </div>

                {/* Content */}
                <div className="flex-grow min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className={`text-sm leading-snug ${!notif.isRead ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
                        {notif.title}
                      </p>
                      {notif.message && (
                        <p className="text-sm text-gray-500 mt-1">{notif.message}</p>
                      )}
                      {notif.relatedPollQuestion && (
                        <p className="text-xs text-gray-400 mt-1 italic">"{notif.relatedPollQuestion}"</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!notif.isRead && (
                        <button
                          onClick={() => markRead(notif.notificationId)}
                          className="p-1.5 text-gray-400 hover:text-green-500 rounded-lg hover:bg-green-50 transition-colors"
                          title="Mark as read"
                          data-testid={`mark-read-${notif.notificationId}`}
                        >
                          <MdCheckCircle className="text-base" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notif.notificationId)}
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                        title="Delete"
                        data-testid={`delete-${notif.notificationId}`}
                      >
                        <MdDelete className="text-base" />
                      </button>
                    </div>
                  </div>

                  {/* Metadata row */}
                  <div className="flex items-center gap-3 mt-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${colors.bg} ${colors.icon}`}>
                      {TYPE_LABELS[notif.type] || notif.type}
                    </span>
                    <span className="text-[11px] text-gray-400">{formatTime(notif.createdAt)}</span>
                    {!notif.isRead && <span className="w-2 h-2 rounded-full bg-primary" />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default NotificationsPage;
